/**
 * <bfx-tooltip> — a reusable anchored tooltip / disclosure (E20-1475).
 *
 * Wraps a trigger button and a panel. The panel opens on hover (mouse/pen), on
 * keyboard focus, and on tap/click (touch), and closes on pointer-out, focus-out,
 * Escape (focus returns to the trigger) and a tap outside. The trigger owns
 * aria-expanded / aria-controls and the panel is inert + aria-hidden while closed; it
 * is nudged inward when centring it would spill past the viewport edge. Progressive
 * enhancement — no panel without JS.
 *
 * The three input models are kept from stepping on each other:
 *   • mouse/pen → hover opens, leave closes (a small delay bridges the trigger→panel gap)
 *   • keyboard  → focus opens ONLY when :focus-visible (so a pointer tap doesn't double-fire)
 *   • touch     → the tap's click toggles; :focus-visible is false, so focus doesn't also open
 *
 * Markup:
 *   <bfx-tooltip>
 *     <button class="bfx-tooltip__trigger" aria-expanded="false" aria-controls="ID">…</button>
 *     <div id="ID" class="bfx-tooltip__panel">…</div>
 *   </bfx-tooltip>
 *
 * Add `inline-with-text` to sit the trigger at the end of the preceding rich text
 * instead of on its own line (E20-1474) — see moveIntoPreviousText below.
 *
 * Add `anchor-row` when the trigger is a small icon inside a narrow container (the cart
 * drawer): the panel then anchors to the nearest positioned ancestor — give the container
 * `position: relative` — starting at its left edge and spanning at most its width, instead of
 * hanging off the icon. It's placed just below the trigger, with the caret under it, so any
 * number of tooltips in one container share an edge and a width. Anchored to the icon and
 * flipped at the viewport edge, a 288px panel can land outside a drawer entirely (E20-1832).
 */
if (!customElements.get('bfx-tooltip')) {
  const CLOSE_DELAY = 120; // ms — keeps hover from flickering across the trigger→panel gap

  // One document-level outside handler shared by every <bfx-tooltip>, rather than a pair
  // of listeners per instance — a reusable tooltip can appear many times on a page (mirrors
  // the shared listener in bfx-promo-message). Only open tooltips are tracked, so this is a
  // no-op while everything is closed.
  const openTooltips = new Set();
  let globalOutsideAttached = false;

  const handleOutsideDismiss = (e) => {
    if (!openTooltips.size) return;
    // Snapshot first: close() mutates the set as we go.
    Array.from(openTooltips).forEach((tip) => {
      if (!tip.contains(e.target)) tip.close();
    });
  };

  const attachGlobalOutside = () => {
    if (globalOutsideAttached) return;
    document.addEventListener('click', handleOutsideDismiss);
    document.addEventListener('touchstart', handleOutsideDismiss, { passive: true });
    globalOutsideAttached = true;
  };

  customElements.define(
    'bfx-tooltip',
    class extends HTMLElement {
      connectedCallback() {
        // Opt-in inline placement. This can't be done in Liquid: the panel is a <div>
        // wrapping a <ul>, and the HTML parser closes an open <p> as soon as it hits
        // either — so server-rendering the host inside the paragraph would silently
        // hoist the panel back out. Moving it after parse is the only way to get the
        // trigger onto the text's last line with the panel still anchored to it.
        if (this.hasAttribute('inline-with-text') && !this.inlined) {
          this.inlined = true;
          // Moving re-enters connectedCallback, which then does the wiring below; the
          // flag stops that recursing, and the AbortController re-bind makes the second
          // pass harmless either way.
          if (this.moveIntoPreviousText()) return;
        }

        this.trigger = this.querySelector('.bfx-tooltip__trigger');
        this.panel = this.querySelector('.bfx-tooltip__panel');
        if (!this.trigger || !this.panel) return;

        // Own the host class so styling never hangs on the consumer remembering it.
        this.classList.add('bfx-tooltip');

        this.isOpen = false;
        this.pinned = false; // a click/tap pins it open; hover stays transient
        this.closeTimer = null;

        // Start closed: inert + hidden from assistive tech until the trigger opens it.
        this.setPanelHidden(true);

        // Re-bind cleanly if the theme editor re-renders the section.
        this.controller?.abort();
        this.controller = new AbortController();
        const { signal } = this.controller;

        this.addEventListener(
          'pointerenter',
          (e) => {
            if (e.pointerType === 'touch') return;
            this.cancelClose();
            this.open();
          },
          { signal }
        );
        this.addEventListener(
          'pointerleave',
          (e) => {
            if (e.pointerType === 'touch') return;
            if (this.pinned) return; // a clicked-open tooltip stays until an explicit close
            // Keyboard focus inside the panel outranks the mouse — don't let a stray
            // pointer-leave collapse it while someone is tabbing the links.
            if (this.contains(document.activeElement)) return;
            this.scheduleClose();
          },
          { signal }
        );

        // focus (not focusin) so we can read :focus-visible off the trigger itself.
        this.trigger.addEventListener(
          'focus',
          () => {
            if (this.trigger.matches(':focus-visible')) this.open();
          },
          { signal }
        );
        this.addEventListener(
          'focusout',
          (e) => {
            if (this.pinned) return;
            if (!this.contains(e.relatedTarget)) this.close();
          },
          { signal }
        );

        // A click/tap pins the panel open (survives hover-out); clicking again closes it.
        this.trigger.addEventListener(
          'click',
          () => {
            this.pinned ? this.close() : this.pin();
          },
          { signal }
        );

        this.addEventListener(
          'keydown',
          (e) => {
            // close() hands focus back to the trigger when focus was inside — which it
            // is here, since Escape only reaches this host listener from within it.
            if (e.key === 'Escape' && this.isOpen) this.close();
          },
          { signal }
        );

        // Dismiss-on-outside is handled by the shared document listener (attached once).
        attachGlobalOutside();
      }

      // Append into the last paragraph of the previous sibling so the trigger reads as
      // part of the sentence ("…built-in touchscreen. View products"). No-ops when there
      // is no paragraph to join, leaving the server-rendered block placement as the
      // fallback. Returns whether the move happened.
      moveIntoPreviousText() {
        const paragraphs = this.previousElementSibling?.querySelectorAll('p');
        const target = paragraphs?.[paragraphs.length - 1];
        if (!target) return false;
        // A text node, not a margin: the gap has to sit inside the line box so it
        // collapses with the wrap like any other word space.
        target.append(' ', this);
        return true;
      }

      disconnectedCallback() {
        this.cancelClose();
        openTooltips.delete(this);
        this.controller?.abort();
      }

      open() {
        if (this.isOpen) return;
        this.isOpen = true;
        openTooltips.add(this);
        this.classList.remove('bfx-tooltip--flip-end');
        this.classList.add('is-open');
        this.trigger.setAttribute('aria-expanded', 'true');
        this.setPanelHidden(false);
        this.positionPanel();
      }

      // Pin it open — stays put through hover-out; only an explicit close (Escape, outside
      // tap, or clicking the trigger again) dismisses it.
      pin() {
        this.pinned = true;
        this.cancelClose();
        this.open();
      }

      close() {
        if (!this.isOpen) return;
        // Read focus placement before inert steals it, so we can hand it back to the
        // trigger rather than letting it drop to <body> on outside-click / Escape.
        const focusWasInside = this.contains(document.activeElement);
        this.isOpen = false;
        this.pinned = false;
        openTooltips.delete(this);
        this.classList.remove('is-open');
        this.trigger.setAttribute('aria-expanded', 'false');
        this.setPanelHidden(true);
        if (focusWasInside) this.trigger.focus();
      }

      scheduleClose() {
        this.cancelClose();
        this.closeTimer = setTimeout(() => this.close(), CLOSE_DELAY);
      }

      cancelClose() {
        clearTimeout(this.closeTimer);
      }

      // Mirror the open state into the a11y tree: a closed panel is inert (out of tab
      // order and pointer reach) and hidden from assistive tech.
      setPanelHidden(hidden) {
        this.panel.inert = hidden;
        this.panel.setAttribute('aria-hidden', String(hidden));
      }

      // Panel opens left-aligned to the trigger; if that spills past the right edge,
      // flip it to open leftward. Visibility (not display) toggles keep the panel laid
      // out, so the rect is valid the moment it opens, before the fade finishes.
      positionPanel() {
        if (this.hasAttribute('anchor-row')) {
          this.placeCaret();
          return;
        }
        const overflowsRight = this.panel.getBoundingClientRect().right > window.innerWidth - 16;
        this.classList.toggle('bfx-tooltip--flip-end', overflowsRight);
      }

      // anchor-row: the panel starts at the container's edge, so drop it just below the trigger
      // and move the caret under it.
      placeCaret() {
        const anchor = this.panel.offsetParent;
        if (!anchor) return;
        // The trigger's bottom edge within the container; the stylesheet adds the gap below it.
        const trigger = this.trigger.getBoundingClientRect();
        const top = trigger.bottom - anchor.getBoundingClientRect().top;
        this.panel.style.setProperty('--bfx-tooltip-top', `${top}px`);

        const caretHalf = parseFloat(getComputedStyle(this.panel, '::before').width) / 2 || 0;
        const centre = trigger.left + trigger.width / 2 - this.panel.getBoundingClientRect().left;
        this.panel.style.setProperty('--bfx-tooltip-caret-left', `${centre - caretHalf}px`);
      }
    }
  );
}
